// @flow

import React from 'react';
import classnames from 'classnames';
import ClientPage from '../context/clientPage';
import ClientConfig from '../context/clientConfig';
import EnhancedResultsTable from './enhancedResultsTable';
import { formatCurrency, formatDate } from '../../helpers/formatUtils';
import i18n from '../../i18n';
import MobileWalletLink from './mobileWalletLink';

const moment = require('moment');
require('moment-timezone');
require('moment/min/locales.min');

type Props = {
  language: string,
  showEnhancedResults: boolean,
  showCardEOV: boolean,
  handleToggle: Function,
  showTransactionTime: string
};

class Results extends React.Component<Props> {
  constructor(props) {
    super(props);
    this.balance = React.createRef();
  }

  componentDidMount(): void {
    const { handleToggle, showEnhancedResults } = this.props;
    if (handleToggle && showEnhancedResults) {
      handleToggle('showEnhancedResults');
    }
    this.balance.current.focus();
  }

  componentDidUpdate(prevProps: Props, snapshot: any): void {
    const { handleToggle } = this.props;
    if (snapshot !== null) {
      handleToggle('showEnhancedResults');
    }
  }

  getSnapshotBeforeUpdate(prevProps: Props): ?boolean {
    const { showEnhancedResults } = this.props;
    if (prevProps.showEnhancedResults !== showEnhancedResults) {
      return showEnhancedResults;
    }
    return null;
  }

  render() {
    const {
      language, showEnhancedResults, showCardEOV, showTransactionTime, showTimeZone
    } = this.props;
    return (
      <ClientConfig.Consumer>
        {clientConfig => {
          const {
            currencySymbolFormatOverride,
            customCardMessage,
            customCardMessageClassNames,
            resultsClassNames,
            resultsOrder,
            resultsHeadingClassNames,
            resultsHeadingText,
            resultsExpiryText,
            showCardNumber,
            showFullCardNumber,
            showCustomCardMessage,
            showEnhancedStore,
            balanceFormatLocaleOverride,
            trnTableDateFormatLocaleOverride,
            defaultEnhancedPageSize,
            showPageSizeOptions,
            gpayImageVariant,
            customNS,
            dateErrorMsg,
          } = clientConfig;

          return (
            <ClientPage.Consumer>
              {({ pageData }) => {
                const {
                  cardNoForDisplay, cardNoFullLength, balance, requestedCurrency,
                  applepassLink, thirdPartyLink, gpayLink, host, cardEOV,
                } = pageData;
                const formattedBalance = formatCurrency(balanceFormatLocaleOverride || language, requestedCurrency, currencySymbolFormatOverride, balance);
                moment.locale(language);
                let transactionTime = moment().format('LLL');
                let timeZone = moment.tz(moment.tz.guess()).format('z');
                if(showTimeZone) transactionTime += ` (${timeZone})`;
                return (
                  <div
                    id='results'
                    className={classnames(resultsClassNames, resultsOrder)}
                  >
                    <div id='balance' className='d-row'>
                      {showTransactionTime && (
                        <p
                          className={`trans-time ${resultsHeadingClassNames}`}
                        >
                          {transactionTime}
                        </p>
                      )}
                      {showCustomCardMessage && (
                        <p
                          className={customCardMessageClassNames}
                        >
                          {i18n.t(customCardMessage, {ns: customNS})}{showCardNumber && cardNoForDisplay}{showFullCardNumber && cardNoFullLength}
                        </p>
                      )}
                      <div id='balanceRow1' ref={this.balance} tabIndex='-1'>
                        <div>
                          <p
                            className={`card-bal ${resultsHeadingClassNames}`}
                          >
                            {i18n.t(resultsHeadingText, {ns: customNS})}
                            {showCardNumber && !showCustomCardMessage && (
                              <span id='last4ofCard'>
                                {' '}
                                {cardNoForDisplay}
                              </span>
                            )}
                            {showFullCardNumber && !showCustomCardMessage && (
                              <span id='fullCardNumber'>
                                {' '}
                                {cardNoFullLength}
                              </span>
                            )}
                          </p>
                        </div>
                        <div>
                          <p className='card-bal-val'>{formattedBalance}</p>
                        </div>
                      </div>
                      {showCardEOV && (
                        <div id='balanceRow2'>
                          <div>
                            <p
                              className={`card-eov ${resultsHeadingClassNames}`}
                            >
                              {i18n.t(resultsExpiryText, {ns: customNS})}
                            </p>
                          </div>
                          <div>
                            <p className='card-eov-val'>
                              {formatDate(customNS, cardEOV, dateErrorMsg, false, language)}
                            </p>
                          </div>
                        </div>
                      )}
                      <div id='balanceRow3'>
                        <MobileWalletLink link={applepassLink} cardNo={cardNoFullLength} customNS={customNS} />
                        <MobileWalletLink link={thirdPartyLink} host={host} cardNo={cardNoFullLength} customNS={customNS} />
                      </div>
                      <div id='balanceRow4'>
                        <MobileWalletLink link={gpayLink} host={host} variant={gpayImageVariant} gpay cardNo={cardNoFullLength} customNS={customNS} />
                      </div>
                    </div>

                    {showEnhancedResults && (
                      <div id='balanceHistory' className='mb-4'>
                        <hr className='hr mt-3' />
                        {pageData.transactions.length > 0 ? (
                          <>
                            <p className='balance-history'>
                              {i18n.t('Balance History', {ns: customNS})}
                            </p>
                            <EnhancedResultsTable
                              data={pageData}
                              language={language}
                              showStore={showEnhancedStore}
                              balanceFormatLocaleOverride={balanceFormatLocaleOverride}
                              defaultEnhancedPageSize={defaultEnhancedPageSize}
                              showPageSizeOptions={showPageSizeOptions}
                              customNS={customNS}
                              currencySymbolFormatOverride={currencySymbolFormatOverride}
                              trnTableDateFormatLocaleOverride={trnTableDateFormatLocaleOverride}
                              dateErrorMsg={dateErrorMsg}
                            />
                          </>
                        ) : (
                          <p className='balance-history'>
                            {i18n.t('No Balance History Found', {ns: customNS})}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              }}
            </ClientPage.Consumer>
          );
        }}
      </ClientConfig.Consumer>
    );
  }
}

export default Results;
